import type { PartnerPromotion, PortalReferral, ReferralPublicStatus } from '@/services/referralsService'

export const PREMIO_MES_META = 8000
export const PREMIO_MES_TITULO = 'Promoção para parceiros'
export const PREMIO_MES_NOME = 'iPhone 18 Pro Max'
export const PREMIO_MES_IMAGEM = '/parceiros-premio-mes.jpg'
export const PREMIO_MES_PERIODO = '01/10 a 30/11'
export const PREMIO_PERIODO_INICIO = '2026-10-01'
export const PREMIO_PERIODO_FIM = '2026-11-30'
export const PREMIO_TICKS = [0, 2000, 4000, 6000, 8000] as const

export const DEFAULT_PARTNER_PROMOTION: PartnerPromotion = {
  id: 'default',
  title: PREMIO_MES_TITULO,
  rewardName: PREMIO_MES_NOME,
  description:
    'Ganha o iPhone quem fechar R$ 8.000 de recorrência mensal entre 01/10 e 30/11. A régua soma o valor mensal dos contratos fechados.',
  bannerText: null,
  imageUrl: PREMIO_MES_IMAGEM,
  targetValue: PREMIO_MES_META,
  startDate: PREMIO_PERIODO_INICIO,
  endDate: PREMIO_PERIODO_FIM,
  isActive: true,
  partnershipType: null,
}

const FECHADOS: ReadonlySet<ReferralPublicStatus> = new Set([
  'ganho',
  'comissao_pendente',
  'comissao_liberada',
  'pago',
])

function dateOnly(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

export function isInPremioPeriod(iso: string, promotion = DEFAULT_PARTNER_PROMOTION) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return false
  const start = dateOnly(promotion.startDate)
  const end = dateOnly(promotion.endDate)
  if (!start || !end) return false
  end.setHours(23, 59, 59, 999)
  return date >= start && date <= end
}

export function mesPremioLabel(promotion = DEFAULT_PARTNER_PROMOTION) {
  const start = dateOnly(promotion.startDate)
  const end = dateOnly(promotion.endDate)
  if (!start || !end) return PREMIO_MES_PERIODO
  const fmt = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' })
  return `${fmt.format(start)} a ${fmt.format(end)}`
}

export function isPromotionCurrent(promotion: PartnerPromotion) {
  const now = new Date()
  const start = dateOnly(promotion.startDate)
  const end = dateOnly(promotion.endDate)
  if (!start || !end) return false
  end.setHours(23, 59, 59, 999)
  return promotion.isActive && now >= start && now <= end
}

export function formatPremioBrl(value: number) {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

function money(value: number) {
  return Math.round(value * 100) / 100
}

export function buildPremioTicks(meta: number) {
  const step = meta / 4
  return [0, step, step * 2, step * 3, meta].map((value) => Math.round(value))
}

export function buildPremioProgress(rows: PortalReferral[], promotion = DEFAULT_PARTNER_PROMOTION) {
  const meta = promotion.targetValue || PREMIO_MES_META
  const doMes = rows.filter((row) => isInPremioPeriod(row.createdAt, promotion))
  const valorFechado = money(
    rows.reduce((sum, row) => {
      if (!FECHADOS.has(row.publicStatus)) return sum
      if (!isInPremioPeriod(row.fechadoEm ?? row.createdAt, promotion)) return sum
      return sum + (row.contratoMensal ?? 0)
    }, 0),
  )
  const faltam = money(Math.max(0, meta - valorFechado))
  const pct = Math.min(100, Math.round((valorFechado / meta) * 100))

  return {
    doMes,
    valorFechado,
    meta,
    faltam,
    pct,
    bateu: valorFechado >= meta,
  }
}
